/* ═════════════════════════════════════════════════════════════════
   Signatory Vault — hot-swap the Executive Director signature
   ═════════════════════════════════════════════════════════════════ */
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ShieldCheck, Upload, Loader2, History, PenLine, Sparkles,
} from "lucide-react";

export function AdminSignatoryVault({ api }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/signatory-vault");
      setMeta(data);
      if (data.cache_bust) setBust(data.cache_bust);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load vault");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);   // eslint-disable-line

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8 MB"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post(
        "/admin/signatory-vault/upload", form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("New signature installed — next Vinci PDF will use it");
      setBust(data.cache_bust || Date.now());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const rollback = async () => {
    if (!window.confirm("Restore the previous Executive Director signature?")) return;
    try {
      const { data } = await api.post("/admin/signatory-vault/rollback");
      toast.success("Rolled back to the previous signature");
      setBust(data.cache_bust || Date.now());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rollback failed");
    }
  };

  const previewSrc = meta?.ok
    ? `${backend}/api/admin/signatory-vault/preview.png?v=${bust}`
    : null;

  return (
    <div data-testid="admin-signatory-vault" className="space-y-6">
      <div>
        <div className="overline text-[#D8B23E] inline-flex items-center gap-2">
          <ShieldCheck size={12} /> Executive Command
        </div>
        <h2 className="font-serif text-3xl mt-2">Signatory Vault</h2>
        <p className="text-[13px] text-[#8A8A82] font-body mt-1 max-w-3xl">
          Hot-swap the <b>Executive Director</b> signature that stamps every Welcome Mandate,
          Compliance Dossier, and Monumental Vellum — no redeploy needed. Upload a clean ink-signature photo;
          Vinci auto-strips paper texture, thickens strokes, and installs it as a bold-black transparent PNG on the gold plate.
        </p>
      </div>

      {loading ? (
        <div className="text-[#8A8A82] py-10 text-center"><Loader2 className="inline animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6">
          {/* Live signature preview */}
          <div className="rounded-sm border border-[#D8B23E]/40 p-6"
                 style={{ background: "linear-gradient(90deg,#0F0C05 0%,#2A1F08 50%,#0F0C05 100%)",
                            boxShadow: "0 30px 80px -30px rgba(216,178,62,0.35)" }}>
            <div className="overline text-[#8A6A1A] mb-2">Live Signature — Gold Plate Preview</div>
            <div className="relative flex items-center justify-center min-h-[220px] rounded-sm p-6"
                   style={{ background: "linear-gradient(120deg,#D8B23E 0%,#F6E4A6 50%,#B87333 100%)" }}>
              {previewSrc ? (
                <img data-testid="signatory-preview-img"
                       src={previewSrc}
                       alt="Executive Director signature"
                       className="max-h-[180px] object-contain" />
              ) : (
                <div className="text-black/70 font-serif italic">No signature installed yet</div>
              )}
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <div className="flex-1">
                <div className="text-[13px] text-[#F5F5F0] font-body">
                  <b>{meta?.signatory_name || "Vinci Inc. — Common Seal"}</b>
                </div>
                <div className="text-[11px] text-[#8A8A82] italic">
                  {meta?.signatory_title || "Executive Director · Asia-Pacific"}
                </div>
              </div>
              {meta?.uploaded_at && (
                <div className="text-[10px] text-[#8A8A82] font-body text-right">
                  Uploaded {new Date(meta.uploaded_at).toLocaleString()}<br />
                  by <b>{meta.uploaded_by || "—"}</b> · {(meta.bytes || 0).toLocaleString()} bytes
                </div>
              )}
            </div>
          </div>

          {/* Upload actions */}
          <motion.div initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                        className="rounded-sm border border-white/10 bg-[#0A0906] p-5 min-w-[280px] flex flex-col gap-3">
            <div>
              <div className="overline text-[#D8B23E] mb-2 flex items-center gap-2">
                <PenLine size={12} /> Install New Signature
              </div>
              <p className="text-[11px] text-[#8A8A82] font-body">
                PNG · JPG · WebP · up to 8 MB. Vinci does the rest — drop the sharpest photo you have.
              </p>
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={upload}
                     data-testid="signatory-upload-input"
                     className="hidden" />
            <button data-testid="signatory-upload-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={busy}
                      className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-4 py-3 rounded-sm inline-flex items-center justify-center gap-1.5">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {busy ? "Processing…" : "Upload New Signature"}
            </button>
            <button data-testid="signatory-rollback-btn"
                      onClick={rollback}
                      className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#C9C9C2] border border-white/20 hover:border-[#D8B23E] px-4 py-2.5 rounded-sm inline-flex items-center justify-center gap-1.5">
              <History size={12} /> Rollback Previous
            </button>
            <div className="pt-3 border-t border-white/10 text-[11px] text-[#8A8A82] font-body">
              <div className="flex items-center gap-1.5 text-[#D8B23E] mb-1">
                <Sparkles size={11} /> Auto-processing pipeline
              </div>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Connected-component filter drops paper-bleed text</li>
                <li>Dilation thickens strokes for the bold-black feel</li>
                <li>Tight crop + transparent PNG for the gold plate</li>
              </ul>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
